import type { ClearEvidenceResult } from 'services/captable';
import { referenceFieldNameSet } from './aiFieldMap';

type AnySecurity = Record<string, any>;

// Drops the rows whose id is in `ids`; an untouched list keeps its identity.
const withoutIds = (refs: any[] | undefined, ids: Set<number>): any[] | undefined =>
  Array.isArray(refs) && refs.some(ref => ids.has(ref?.id)) ? refs.filter(ref => !ids.has(ref?.id)) : refs;

// Drops the AI suggestion entries stored under any of these field names; untouched findings keep their identity.
const withoutSuggestions = (aiFinding: any, fieldNames: Set<string> | undefined): any => {
  const metadata = aiFinding?.field_metadata;
  if (!fieldNames || !metadata || !Object.keys(metadata).some(key => fieldNames.has(key))) {
    return aiFinding;
  }
  return {
    ...aiFinding,
    field_metadata: Object.fromEntries(Object.entries(metadata).filter(([key]) => !fieldNames.has(key))),
  };
};

/** Mirrors a server clear onto the cached securities, so a grid rebuild keeps the cleared cells empty. */
export const applyClearToSecurities = (
  securities: AnySecurity[],
  result: Partial<ClearEvidenceResult>
): AnySecurity[] => {
  const citationIds = new Set(result.retired_citation_ids ?? []);
  const mappingIds = new Set(result.retired_mapping_ids ?? []);
  const clearedFields = new Map<number, Set<string>>();
  (result.cleared_cells ?? []).forEach(({ security_id: securityId, field_name: fieldName }) => {
    clearedFields.set(
      securityId,
      new Set([...(clearedFields.get(securityId) ?? []), ...referenceFieldNameSet([fieldName])])
    );
  });

  const next = securities.map(security => {
    const citations = withoutIds(security.citation_references, citationIds);
    const mappings = withoutIds(security.mapping_references, mappingIds);
    const aiFinding = withoutSuggestions(security.ai_finding, clearedFields.get(security.id));
    const unchanged =
      citations === security.citation_references &&
      mappings === security.mapping_references &&
      aiFinding === security.ai_finding;
    return unchanged
      ? security
      : { ...security, citation_references: citations, mapping_references: mappings, ai_finding: aiFinding };
  });
  return next.every((security, index) => security === securities[index]) ? securities : next;
};
